import React, { useEffect, useState } from "react";
import i18n from "../../../language/i18n";
import { confirmTypeTagReview, loadTypeTags, typeTagLabel, TypeTag } from "../../../services/typeTagApi";

type Review = { status?: string; suggestedIds?: string[] } | null | undefined;

/**
 * Shown on Edit when the tag backfill could not decide a product's tags: it lists what it suggests
 * and lets a person take them in one tap. Picking tags by hand and saving settles it too.
 */
const TypeTagReviewBanner = ({
  review,
  productUniqueId,
  onConfirmed,
}: {
  review: Review;
  productUniqueId?: string;
  onConfirmed: (ids: string[]) => void;
}) => {
  const [tags, setTags] = useState<TypeTag[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [dismissed, setDismissed] = useState(false);
  const pending = review?.status === "PENDING" && (review.suggestedIds || []).length > 0;

  useEffect(() => {
    if (pending) loadTypeTags().then((rows) => setTags(rows || []));
  }, [pending]);

  if (!pending || dismissed) return null;
  const suggested = (review?.suggestedIds || []).map((id) => tags.find((t) => t.id === id)).filter(Boolean) as TypeTag[];
  if (!suggested.length) return null;

  const confirm = async () => {
    if (!productUniqueId || busy) return;
    setBusy(true);
    setError("");
    const ids = suggested.map((t) => t.id);
    const result = await confirmTypeTagReview(productUniqueId, ids);
    setBusy(false);
    if (result.ok) {
      onConfirmed(ids);
      setDismissed(true);
    } else {
      setError(result.message);
    }
  };

  return (
    <div className="ps-note ps-note--warn ps-tags__review" role="status">
      <b>Check these tags.</b> From the name we think: {suggested.map((t) => typeTagLabel(t, i18n.language)).join(", ")}.
      <div className="ps-tags__form-actions">
        <button type="button" className="ps-btn ps-btn--blue ps-btn--sm" disabled={busy || !productUniqueId} onClick={confirm}>
          <span>{busy ? "Saving…" : "Use these tags"}</span>
        </button>
        <button type="button" className="ps-btn ps-btn--line ps-btn--sm" onClick={() => setDismissed(true)}>
          <span>I’ll choose myself</span>
        </button>
      </div>
      {error ? <p role="alert" className="mb-0">{error}</p> : null}
    </div>
  );
};

export default TypeTagReviewBanner;
